/**
 * Result from component type detection
 */
export interface DetectionResult {
  /** The detected component type (e.g., 'text', 'button', 'line-chart') */
  detectedType: string;
  /** Confidence score between 0 and 1 */
  confidence: number;
  /** Human-readable reasoning for the detection */
  reasoning: string;
  /** Keywords that contributed to the detection */
  keywords: string[];
}

/**
 * Service for detecting component types from natural language descriptions
 * Uses keyword matching and pattern recognition to identify the most appropriate
 * component type based on user input
 */
export class ComponentTypeDetector {
  private static keywordPatterns = {
    text: {
      keywords: ['title', 'heading', 'label', 'text', 'description', 'paragraph', 'caption', 'subtitle', 'header'],
      patterns: [
        /\btitle\b/i,
        /\bheading\b/i,
        /\blabel\b/i,
        /\btext that (says|reads|displays)\b/i,
        /\bdescription\b/i,
        /\bparagraph\b/i,
      ],
      priority: 1,
    },
    button: {
      keywords: ['button', 'click', 'action', 'submit', 'save', 'cancel', 'confirm', 'trigger', 'press'],
      patterns: [
        /\bbutton\b/i,
        /\bclick\b/i,
        /\bsubmit\b/i,
        /\baction button\b/i,
        /\btrigger\b/i,
      ],
      priority: 2,
    },
    input: {
      keywords: ['input', 'field', 'textbox', 'search', 'entry', 'form', 'type', 'enter'],
      patterns: [
        /\binput\b/i,
        /\btext field\b/i,
        /\bform field\b/i,
        /\bsearch (bar|box|field)\b/i,
        /\bdata entry\b/i,
      ],
      priority: 2,
    },
    image: {
      keywords: ['image', 'picture', 'photo', 'icon', 'logo', 'graphic', 'illustration'],
      patterns: [
        /\bimage\b/i,
        /\bpicture\b/i,
        /\bphoto\b/i,
        /\bicon\b/i,
        /\blogo\b/i,
      ],
      priority: 2,
    },
    container: {
      keywords: ['container', 'section', 'panel', 'box', 'group', 'area', 'wrapper', 'div'],
      patterns: [
        /\bcontainer\b/i,
        /\bsection\b/i,
        /\bpanel\b/i,
        /\bbox\b/i,
        /\bgroup\b/i,
      ],
      priority: 3,
    },
    'flex-container': {
      keywords: ['flex', 'flexible', 'responsive', 'layout', 'grid', 'columns', 'rows'],
      patterns: [
        /\bflex(ible)?\s+(container|layout|box)\b/i,
        /\bresponsive\s+(container|layout)\b/i,
        /\bgrid\s+layout\b/i,
        /\bcolumn layout\b/i,
        /\brow layout\b/i,
      ],
      priority: 3,
    },
    list: {
      keywords: ['list', 'items', 'bullet', 'numbered', 'todo', 'checklist', 'options'],
      patterns: [
        /\blist\b(?!.*table)/i,
        /\bbullet(ed)?\s+(list|points)\b/i,
        /\bnumbered list\b/i,
        /\btodo list\b/i,
        /\bchecklist\b/i,
      ],
      priority: 2,
    },
    table: {
      keywords: ['table', 'grid', 'spreadsheet', 'data', 'rows', 'columns', 'cells'],
      patterns: [
        /\btable\b/i,
        /\bdata table\b/i,
        /\bin\s+(a\s+)?table\b/i,
        /\btable\s+format\b/i,
        /\bspreadsheet\b/i,
        /\brows and columns\b/i,
        /\btabular data\b/i,
      ],
      priority: 1,  // Increased priority to match charts
    },
    'kanban-board': {
      keywords: ['kanban', 'board', 'task board', 'pipeline', 'status board', 'sprint board', 'workflow board'],
      patterns: [
        /\bkanban\b/i,
        /\btask\s+board\b/i,
        /\bpipeline\s+board\b/i,
        /\bstatus\s+board\b/i,
        /\bsprint\s+board\b/i,
        /\bproject\s+board\b/i,
        /\bworkflow\s+board\b/i,
        /\bsales\s+pipeline\b/i,
      ],
      priority: 2,
    },
    'line-chart': {
      keywords: ['line chart', 'trend', 'time series', 'over time', 'progression', 'timeline', 'sensor'],
      patterns: [
        /\bline\s+(chart|graph)\b/i,
        /\btrend\s+(chart|graph|line)\b/i,
        /\bover time\b/i,
        /\btime series\b/i,
        /\bsensor (readings?|data)\b/i,
        /\btemperature (chart|graph|over)\b/i,
      ],
      priority: 1,
    },
    'bar-chart': {
      keywords: ['bar chart', 'bar graph', 'comparison', 'categories', 'histogram'],
      patterns: [
        /\bbar\s+(chart|graph)\b/i,
        /\bcompar(e|ison)\s+(chart|graph)\b/i,
        /\bhistogram\b/i,
        /\bcategory comparison\b/i,
      ],
      priority: 1,
    },
    'pie-chart': {
      keywords: ['pie chart', 'distribution', 'percentage', 'proportion', 'parts', 'breakdown'],
      patterns: [
        /\bpie\s+(chart|graph)\b/i,
        /\bdistribution\s+(chart|graph)\b/i,
        /\bpercentage\s+(chart|graph|breakdown)\b/i,
        /\bproportion\s+(chart|graph)\b/i,
      ],
      priority: 1,
    },
    'vertical-gauge-chart': {
      keywords: [
        'vertical gauge',
        'tank gauge',
        'tank level',
        'fill level',
        'liquid level',
        'vertical meter',
        'tank meter',
        'level indicator',
        'fill indicator',
        'vertical bar gauge',
        'tank fill',
        'capacity gauge',
        'reservoir level',
        'storage level',
      ],
      patterns: [
        /\bvertical\s+gauge\b/i,
        /\btank\s+(gauge|level|meter|indicator)\b/i,
        /\bfill\s+(level|gauge|indicator)\b/i,
        /\bliquid\s+(level|gauge|meter)\b/i,
        /\blevel\s+(gauge|indicator|meter)\b/i,
        /\bvertical\s+(bar\s+)?(gauge|meter)\b/i,
        /\bcapacity\s+(gauge|indicator|meter)\b/i,
        /\breservoir\s+(level|gauge)\b/i,
        /\bstorage\s+(level|gauge)\b/i,
      ],
      priority: 1,
    },
    'radial-dial': {
      keywords: ['radial dial', 'dial control', 'dimmer', 'rotary',
                 'radial control', 'knob control', 'circular control', 'radial slider'],
      patterns: [
        /\bradial\s+dial\b/i,
        /\bdial\s+control\b/i,
        /\bdimmer\b/i,
        /\brotary\s+(control|dial|knob)\b/i,
        /\bradial\s+(control|slider|knob)\b/i,
        /\bcircular\s+(control|slider|dial)\b/i,
      ],
      priority: 2,
    },
    'toggle-switch': {
      keywords: ['toggle switch', 'toggle control', 'on off switch', 'switch control', 'power switch'],
      patterns: [
        /\btoggle\s+switch\b/i,
        /\bon\s*\/?\s*off\s+switch\b/i,
        /\bpower\s+switch\b/i,
      ],
      priority: 2,
    },
    'status-light': {
      keywords: ['status light', 'status indicator', 'led indicator', 'status led', 'indicator light', 'signal light'],
      patterns: [
        /\bstatus\s+light\b/i,
        /\bstatus\s+indicator\b/i,
        /\bled\s+indicator\b/i,
        /\bindicator\s+light\b/i,
        /\bsignal\s+light\b/i,
      ],
      priority: 2,
    },
    'push-button': {
      keywords: ['push button', 'momentary button', 'press button', 'power button', 'push control'],
      patterns: [
        /\bpush\s+button\b/i,
        /\bmomentary\s+button\b/i,
        /\bpress\s+button\b/i,
      ],
      priority: 2,
    },
    'icon-button': {
      keywords: ['icon button', 'icon control', 'icon toggle'],
      patterns: [
        /\bicon\s+button\b/i,
        /\bicon\s+toggle\b/i,
      ],
      priority: 2,
    },
    'stepper': {
      keywords: ['stepper', 'stepper control', 'number stepper', 'incrementor', 'counter', 'plus minus'],
      patterns: [
        /\bstepper\b/i,
        /\bnumber\s+stepper\b/i,
        /\bplus\s*\/?\s*minus\b/i,
        /\bincrement(?:or)?\b/i,
      ],
      priority: 2,
    },
    'slider': {
      keywords: ['slider', 'range slider', 'slide control', 'range control', 'slider control'],
      patterns: [
        /\bslider\b/i,
        /\brange\s+slider\b/i,
        /\bslide\s+control\b/i,
      ],
      priority: 2,
    },
    'level-indicator': {
      keywords: ['level indicator', 'level gauge', 'battery indicator', 'signal meter', 'fill level', 'level bar'],
      patterns: [
        /\blevel\s+indicator\b/i,
        /\bbattery\s+(?:indicator|gauge)\b/i,
        /\bsignal\s+(?:meter|bars?)\b/i,
        /\bfill\s+level\b/i,
      ],
      priority: 2,
    },
    'segmented-control': {
      keywords: ['segmented control', 'segmented', 'tab bar', 'tab selector', 'option selector'],
      patterns: [
        /\bsegmented?\s*(?:control)?\b/i,
        /\btab\s*(?:bar|selector)\b/i,
      ],
      priority: 2,
    },
    'color-picker': {
      keywords: ['color picker', 'colour picker', 'color wheel', 'color selector', 'color chooser'],
      patterns: [
        /\bcolou?r\s*(?:pick|wheel|select|choos)/i,
      ],
      priority: 2,
    },
    'thermostat': {
      keywords: ['thermostat', 'hvac', 'temperature control', 'temp control', 'climate control', 'heating cooling'],
      patterns: [
        /\bthermostat\b/i,
        /\bhvac\s*control\b/i,
        /\btemp(?:erature)?\s*control\b/i,
        /\bclimate\s*control\b/i,
      ],
      priority: 2,
    },
    'gauge-chart': {
      keywords: [
        'gauge', 'meter', 'dial', 'speedometer',
        'progress ring', 'progress bar', 'progress indicator', 'milestone tracker',
        'completion ring', 'circular progress', 'concentric ring',
      ],
      patterns: [
        /\bgauge\b/i,
        /\bmeter\b/i,
        /\bdial\b/i,
        /\bspeedometer\b/i,
        /\bkpi\s+(indicator|display|gauge)\b/i,
        /\bprogress\s+(ring|bar|indicator)\b/i,
        /\bmilestone\s+track(er)?\b/i,
        /\bcircular\s+progress\b/i,
        /\bconcentric\s+ring/i,
        /\bcompletion\s+ring\b/i,
      ],
      priority: 1,
    },
    'area-chart': {
      keywords: [
        'area chart', 'area graph', 'filled chart', 'filled graph',
        'cumulative', 'cumulative chart', 'stacked area', 'stacked chart',
        'shaded chart', 'shaded graph', 'filled trend', 'trend area',
        'area plot', 'fill chart'
      ],
      patterns: [
        /\barea\s+(chart|graph|plot)\b/i,
        /\bfilled\s+(chart|graph|trend|line)\b/i,
        /\bcumulative\s+(chart|graph|trend|data)\b/i,
        /\bstacked\s+area\b/i,
        /\bshaded\s+(chart|graph|area)\b/i,
        /\btrend\s+area\b/i,
        /\barea\s+trend\b/i,
        /\bfill(ed)?\s+(under|beneath|below)\b/i,
      ],
      priority: 1,
    },
    'scatter-chart': {
      keywords: ['scatter', 'plot', 'correlation', 'distribution points', 'xy chart'],
      patterns: [
        /\bscatter\s+(plot|chart|graph)\b/i,
        /\bcorrelation\s+(chart|graph)\b/i,
        /\bxy\s+(chart|graph|plot)\b/i,
        /\bpoint distribution\b/i,
      ],
      priority: 1,
    },
    'heatmap-chart': {
      keywords: [
        'heatmap', 'heat map', 'density', 'intensity map', 'color map',
        'resource matrix', 'workload matrix', 'team heatmap', 'capacity grid',
        'availability matrix', 'skill matrix', 'coverage matrix',
      ],
      patterns: [
        /\bheat\s?map\b/i,
        /\bdensity\s+(map|chart|graph)\b/i,
        /\bintensity map\b/i,
        /\bcolor map\b/i,
        /\bresource\s+matrix\b/i,
        /\bworkload\s+(matrix|heatmap|grid)\b/i,
        /\b(team|staff|capacity|availability|skill|coverage)\s+(matrix|heatmap|grid)\b/i,
      ],
      priority: 1,
    },
    'treemap-chart': {
      keywords: ['treemap', 'tree map', 'hierarchical', 'nested rectangles'],
      patterns: [
        /\btree\s?map\b/i,
        /\bhierarchical\s+(chart|graph|view)\b/i,
        /\bnested rectangles\b/i,
      ],
      priority: 1,
    },
    'file-storage': {
      keywords: ['file', 'upload', 'attachment', 'document manager', 'file manager', 'file browser', 'file storage', 'media library'],
      patterns: [
        /\bfile\s+(upload|storage|manager|browser)\b/i,
        /\bupload\s+(file|document|attachment|media)\b/i,
        /\battachment\s*(manager|list)?\b/i,
        /\bdocument\s+(storage|manager|upload)\b/i,
        /\bmedia\s+library\b/i,
      ],
      priority: 2,
    },
    'modal': {
      keywords: ['modal', 'dialog', 'popup', 'overlay', 'lightbox', 'popover'],
      patterns: [
        /\bmodal\b/i,
        /\bdialog\s*(box)?\b/i,
        /\bpopup\s*(window|overlay)?\b/i,
        /\boverlay\s*(panel|dialog)?\b/i,
        /\blightbox\b/i,
      ],
      priority: 2,
    },
    'checklist': {
      keywords: ['checklist', 'todo list', 'task list', 'check list', 'to-do', 'checkbox', 'todo'],
      patterns: [
        /\bchecklist\b/i,
        /\btodo\s*list\b/i,
        /\btask\s*list\b/i,
        /\bcheck\s*list\b/i,
      ],
      priority: 2,
    },
    'horizontal-timeline': {
      keywords: ['timeline', 'horizontal timeline', 'roadmap', 'milestone', 'milestones', 'phase timeline'],
      patterns: [
        /\btimeline\b/i,
        /\broadmap\b/i,
        /\bmilestone\s*(timeline)?\b/i,
        /\bphase\s*timeline\b/i,
      ],
      priority: 2,
    },
    'gantt-chart': {
      keywords: ['gantt', 'gantt chart', 'project timeline', 'task timeline', 'schedule chart', 'dependency chart'],
      patterns: [
        /\bgantt\s*(chart)?\b/i,
        /\bproject\s+timeline\b/i,
        /\btask\s+timeline\b/i,
        /\bschedule\s+chart\b/i,
        /\bdependency\s+chart\b/i,
      ],
      priority: 2,
    },
    'assignment-widget': {
      keywords: ['assignment', 'team widget', 'team members', 'assignments', 'team assignments', 'people widget', 'team roster'],
      patterns: [
        /\bassignment\s*(widget)?\b/i,
        /\bteam\s*(widget|assignments?|roster|members)\b/i,
        /\bpeople\s*widget\b/i,
      ],
      priority: 2,
    },
    'calendar': {
      keywords: ['calendar', 'event calendar', 'schedule', 'scheduling', 'appointments', 'agenda', 'day planner', 'week planner', 'month view'],
      patterns: [
        /\bcalendar\b/i,
        /\bevent\s+calendar\b/i,
        /\bschedul(e|ing)\s+(view|component|widget)\b/i,
        /\bappointment\s+(calendar|scheduler)\b/i,
        /\bagenda\s+(view|component)\b/i,
        /\bday\s+planner\b/i,
        /\bweek\s+planner\b/i,
      ],
      priority: 2,
    },
    'project-hub': {
      keywords: ['project hub', 'project board', 'multi-view board', 'github projects', 'multi-view project', 'project views'],
      patterns: [
        /\bproject\s+hub\b/i,
        /\bmulti.?view\s+(project|board)\b/i,
        /\bgithub.?style\s+project\b/i,
        /\bproject\s+board\s+with\s+(table|kanban)\b/i,
      ],
      priority: 2,
    },
    'questionnaire': {
      keywords: ['questionnaire', 'survey', 'quiz', 'intake form', 'onboarding form', 'feedback form', 'poll form', 'food preferences', 'preference survey', 'multi-step form'],
      patterns: [
        /\bquestionnaire\b/i,
        /\bsurvey\b/i,
        /\bquiz\b/i,
        /\bintake\s+form\b/i,
        /\bonboarding\s+form\b/i,
        /\bfeedback\s+form\b/i,
        /\bpreference\s+(survey|form)\b/i,
        /\bmulti.?step\s+form\b/i,
      ],
      priority: 2,
    },
    'voting-card': {
      keywords: ['vote', 'voting', 'poll', 'ballot', 'approve', 'veto', 'approval card', 'decision card', 'consensus', 'polling', 'election'],
      patterns: [
        /\bvot(?:e|ing)\b/i,
        /\bpoll(?:ing)?\b/i,
        /\bballot\b/i,
        /\bapprov(?:e|al)\s*(?:card|vote)?\b/i,
        /\bveto\b/i,
        /\bdecision\s+card\b/i,
        /\bconsensus\b/i,
      ],
      priority: 2,
    },
    'time-tracker': {
      keywords: ['time tracker', 'timesheet', 'time tracking', 'timer', 'toggl', 'time log', 'hours tracker', 'billable hours', 'time entry', 'work hours'],
      patterns: [
        /time\s*track/i,
        /time\s*sheet/i,
        /hour(s|ly)?\s*(track|log)/i,
        /billable\s*(hours|time)/i,
        /work\s*timer/i,
      ],
      priority: 2,
    },
  };

  private static agriculturalContextKeywords = {
    sensor: ['sensor', 'temperature', 'humidity', 'moisture', 'ph', 'npk', 'nutrient'],
    metrics: ['yield', 'harvest', 'growth', 'irrigation', 'fertilizer', 'pesticide'],
    equipment: ['equipment', 'machinery', 'tractor', 'pump', 'sprinkler'],
    field: ['field', 'plot', 'zone', 'section', 'acre', 'hectare'],
  };

  /**
   * Detects the most appropriate component type from a natural language description
   * @param description - The user's natural language description
   * @returns Detection result with type, confidence, and reasoning
   */
  static detect(description: string): DetectionResult {
    if (!description || description.trim().length === 0) {
      return {
        detectedType: 'text',
        confidence: 0.3,
        reasoning: 'Empty or minimal description, defaulting to text',
        keywords: [],
      };
    }

    const lowerDescription = description.toLowerCase();
    const detectedKeywords: string[] = [];
    const scores: Map<string, number> = new Map();

    // Check for exact matches and patterns
    for (const [componentType, config] of Object.entries(this.keywordPatterns)) {
      let score = 0;
      const matchedKeywords: string[] = [];

      // Check keywords
      for (const keyword of config.keywords) {
        if (lowerDescription.includes(keyword)) {
          score += 2;
          matchedKeywords.push(keyword);
        }
      }

      // Check patterns
      for (const pattern of config.patterns) {
        const match = lowerDescription.match(pattern);
        if (match) {
          score += 3; // Patterns are more specific, so they get higher weight
          matchedKeywords.push(match[0]);
        }
      }

      // Apply priority boost
      score *= (4 - config.priority); // Higher priority (lower number) gets more weight

      if (score > 0) {
        scores.set(componentType, score);
        detectedKeywords.push(...matchedKeywords);
      }
    }

    // Check for agricultural context to boost relevant chart types
    let hasAgriculturalContext = false;
    for (const keywords of Object.values(this.agriculturalContextKeywords)) {
      if (keywords.some(keyword => lowerDescription.includes(keyword))) {
        hasAgriculturalContext = true;
        break;
      }
    }

    // Boost chart types if agricultural context is detected
    if (hasAgriculturalContext) {
      if (lowerDescription.includes('over time') || lowerDescription.includes('trend')) {
        const lineScore = scores.get('line-chart') || 0;
        scores.set('line-chart', lineScore + 5);
      }
      if (lowerDescription.includes('status') || lowerDescription.includes('level')) {
        const gaugeScore = scores.get('gauge-chart') || 0;
        scores.set('gauge-chart', gaugeScore + 3);
      }
    }

    // Special case: if "chart" or "graph" is mentioned without specific type
    if ((lowerDescription.includes('chart') || lowerDescription.includes('graph')) && scores.size === 0) {
      // Try to infer from context
      // Score 7 → confidence 0.7, which meets the 0.6 threshold in AIPropertiesChat
      if (lowerDescription.includes('over time') || lowerDescription.includes('trend')) {
        scores.set('line-chart', 7);
      } else if (lowerDescription.includes('compare') || lowerDescription.includes('comparison')) {
        scores.set('bar-chart', 7);
      } else if (lowerDescription.includes('distribution') || lowerDescription.includes('percentage')) {
        scores.set('pie-chart', 7);
      } else {
        // Default to line chart for generic chart requests
        scores.set('line-chart', 7);
      }
    }

    // Find the highest scoring component type
    let bestType = 'text';
    let bestScore = 0;
    for (const [type, score] of scores.entries()) {
      if (score > bestScore) {
        bestScore = score;
        bestType = type;
      }
    }

    // Calculate confidence based on score
    let confidence = Math.min(0.9, bestScore / 10);

    // If no keywords matched, use basic heuristics
    if (bestScore === 0) {
      bestType = 'text';
      confidence = 0.4;
      return {
        detectedType: bestType,
        confidence,
        reasoning: 'No specific component indicators found, defaulting to text',
        keywords: [],
      };
    }

    // Generate reasoning
    const reasoning = this.generateReasoning(bestType, detectedKeywords, hasAgriculturalContext);

    return {
      detectedType: bestType,
      confidence,
      reasoning,
      keywords: [...new Set(detectedKeywords)], // Remove duplicates
    };
  }

  private static generateReasoning(
    type: string,
    keywords: string[],
    hasAgriculturalContext: boolean
  ): string {
    const typeLabels: Record<string, string> = {
      'text': 'Text Component',
      'button': 'Button',
      'input': 'Input Field',
      'image': 'Image',
      'container': 'Container',
      'flex-container': 'Flexible Container',
      'list': 'List',
      'table': 'Table',
      'line-chart': 'Line Chart',
      'bar-chart': 'Bar Chart',
      'pie-chart': 'Pie Chart',
      'vertical-gauge-chart': 'Vertical Gauge Chart',
      'radial-dial': 'Radial Dial Control',
      'toggle-switch': 'Toggle Switch',
      'status-light': 'Status Light Indicator',
      'push-button': 'Push Button',
      'icon-button': 'Icon Button',
      'stepper': 'Stepper Control',
      'slider': 'Slider Control',
      'level-indicator': 'Level Indicator',
      'segmented-control': 'Segmented Control',
      'color-picker': 'Color Picker',
      'thermostat': 'Thermostat',
      'gauge-chart': 'Gauge Chart',
      'area-chart': 'Area Chart',
      'scatter-chart': 'Scatter Chart',
      'heatmap-chart': 'Heatmap / Resource Matrix',
      'treemap-chart': 'Treemap',
      'file-storage': 'File Storage',
      'kanban-board': 'Kanban Board',
      'modal': 'Modal',
      'checklist': 'Checklist',
      'horizontal-timeline': 'Timeline',
      'assignment-widget': 'Assignment Widget',
      'gantt-chart': 'Gantt Chart',
      'calendar': 'Calendar',
      'project-hub': 'Project Hub',
      'questionnaire': 'Questionnaire',
      'voting-card': 'Voting Card',
      'time-tracker': 'Time Tracker',
    };

    let reasoning = `Detected as ${typeLabels[type] || type}`;

    if (keywords.length > 0) {
      reasoning += ` based on keywords: "${keywords.slice(0, 3).join('", "')}"`;
    }

    if (hasAgriculturalContext) {
      reasoning += ' (agricultural context detected)';
    }

    return reasoning;
  }

  /**
   * Gets a user-friendly suggestion text for a component type
   * @param type - The component type
   * @returns User-friendly description of what will be created
   */
  static getSuggestionText(type: string): string {
    const suggestions: Record<string, string> = {
      'text': 'Will create a text element with your content',
      'button': 'Will create an interactive button',
      'input': 'Will create an input field for data entry',
      'image': 'Will create an image placeholder',
      'container': 'Will create a container to group elements',
      'flex-container': 'Will create a flexible layout container',
      'list': 'Will create a list of items',
      'table': 'Will create a data table',
      'line-chart': 'Will create a line chart for trends over time',
      'bar-chart': 'Will create a bar chart for comparisons',
      'pie-chart': 'Will create a pie chart for distributions',
      'vertical-gauge-chart': 'Will create a vertical tank gauge for level indicators',
      'radial-dial': 'Will create an interactive radial dial control',
      'toggle-switch': 'Will create an on/off toggle switch control',
      'status-light': 'Will create a status light indicator with color-coded states',
      'push-button': 'Will create a momentary push button control',
      'icon-button': 'Will create an icon-based toggle button',
      'stepper': 'Will create a plus/minus stepper control',
      'slider': 'Will create a range slider control',
      'level-indicator': 'Will create a segmented level indicator display',
      'segmented-control': 'Will create a segmented control for option selection',
      'color-picker': 'Will create an interactive color picker',
      'thermostat': 'Will create a thermostat control with temperature display and mode selection',
      'gauge-chart': 'Will create a gauge for single metrics',
      'area-chart': 'Will create an area chart for cumulative data',
      'scatter-chart': 'Will create a scatter plot for correlations',
      'heatmap-chart': 'Will create a heatmap for density visualization or resource matrix',
      'treemap-chart': 'Will create a treemap for hierarchical data',
      'file-storage': 'Will create a file storage component for uploading and managing files',
      'checklist': 'Will create an interactive checklist with checkboxes, progress tracking, and priority indicators',
      'horizontal-timeline': 'Will create a timeline for milestones and project phases',
      'assignment-widget': 'Will create a team assignment display with avatars, roles, and task counts',
      'gantt-chart': 'Will create a Gantt chart for project scheduling with task bars, dependencies, and milestones',
      'calendar': 'Will create an event calendar with month, week, day, and agenda views',
      'project-hub': 'Will create a multi-view project board with Table, Board, Timeline, and Calendar tabs',
      'questionnaire': 'Will create a multi-step questionnaire with branching logic, per-user responses, and results aggregation',
      'voting-card': 'Will create a voting card with approve/veto options, resolution logic, real-time vote tracking, and deadline support',
      'time-tracker': 'Will create a time tracker with running timer, client/project hierarchy, hourly rates, calendar view, reports, and PDF invoice generation',
    };

    return suggestions[type] || 'Will create a component based on your description';
  }
}
